// [rail epic] DraftRailEditor — a lightweight edit surface for a Workspace
// draft opened in Home's rail. Deliberately NOT DraftDetail's full Editor:
// that component mounts its own Chat/Panels rail (S-517), and nesting it
// inside Home's OWN rail would nest rail-inside-rail. This binds only the
// current version's content to the SAME EditorFrame/TemplateEditor
// primitives DraftDetail's Editor already uses, plus the same
// useSaveVersion mutation — version history, citations, and review stay on
// the full route. The header's existing "Open in new tab" link (RecordDrawer.tsx)
// already covers that escape hatch, so this component doesn't repeat it.
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from "react";
import { Alert, Button, EditorFrame, LoadingState, useToast } from "../../components";
import { getApiErrorMessage } from "../../data/api";
import { useSession } from "../../auth/AuthProvider";
import {
  useDraft,
  useDraftVersion,
  useSaveVersion,
  type Draft,
  type DraftCitation,
} from "../../data/draftsQuery";
import { useRecordDrawer } from "./RecordDrawer";
import { useAgentDraftLock } from "./agentDraftLock";
import {
  subscribeReviewFocus,
  getReviewFocus,
  type ReviewFocus,
} from "./review";

export function DraftRailEditor({ id }: { id: string }): JSX.Element {
  const { data: draft, isLoading, isError, error } = useDraft(id);

  if (isLoading) {
    return <LoadingState label="Loading draft…" />;
  }
  if (isError) {
    return (
      <Alert label="Couldn't load draft" tone="error">
        {getApiErrorMessage(error) ?? error.message}
      </Alert>
    );
  }
  if (draft === undefined) return <></>;

  return <RailEditor draft={draft} />;
}

function RailEditor({ draft }: { draft: Draft }): JSX.Element {
  const session = useSession();
  const isOwner = draft.ownerUid === session?.uid;
  const myCollab = draft.sharedWith.find((c) => c.uid === session?.uid);
  const canWrite = isOwner || myCollab?.permission === "write";
  // Read-only while the agent may be writing this draft; what the user typed
  // stays in the editor and saves once the lock lifts.
  const lock = useAgentDraftLock(draft.id);
  const writable = canWrite && lock === null;

  // Seed from the current version's content, same "adjust state during
  // render" pattern DraftDetail's Editor uses — CKEditor only reads `data`
  // at mount, and the version arrives asynchronously.
  const hasVersion = draft.currentVersion > 0;
  const version = useDraftVersion(draft.id, hasVersion ? draft.currentVersion : undefined);
  const ready = !hasVersion || version.data !== undefined;
  const loadedVersion = hasVersion ? (version.data?.version ?? 0) : 0;
  const [content, setContent] = useState("");
  // [Fix 5] The draft's inline context citations ([n] markers → the emails /
  // transcripts / chats a term came from). The clause-review rail used to drop
  // these (it only seeded content), so a $-figure's source was unreachable
  // mid-review. Seed them alongside content and hand them to the editor, which
  // marks + wires them exactly as the full Draft Workspace does.
  const [citations, setCitations] = useState<DraftCitation[]>([]);
  const [seededVersion, setSeededVersion] = useState<number | null>(null);
  if (ready && seededVersion !== loadedVersion) {
    setSeededVersion(loadedVersion);
    setContent(version.data?.content ?? "");
    setCitations(version.data?.citations ?? []);
  }
  const { openRecord } = useRecordDrawer();

  // [after-review-inline P2b] Scroll + outline the clause under review when the
  // inline chat publishes it (and this rail is showing that draft).
  const hostRef = useReviewFocusScroll(draft.id);

  const save = useSaveVersion();
  const toast = useToast();
  const handleSave = (): void => {
    if (!writable || save.isPending) return;
    save.mutate(
      { draftId: draft.id, content },
      {
        onSuccess: (v) => {
          toast.push({ variant: "success", title: `Saved v${v.version}` });
        },
      }
    );
  };

  return (
    <div className="dre" ref={hostRef}>
      {!canWrite && (
        <div className="dft__readonly" role="note">
          You have read-only access to this draft.
        </div>
      )}
      {canWrite && lock !== null && (
        <div className="dft__readonly" role="note" data-testid="draft-agent-lock">
          {lock}
        </div>
      )}
      {ready ? (
        <EditorFrame
          bounded
          compact
          value={content}
          citations={citations}
          onOpenCitation={(c) => openRecord(c.citation.recordRef, c.citation.quote)}
          onChange={setContent}
          readOnly={!writable}
          ariaLabel="Draft content editor"
          placeholder="Paste or type the contract draft…"
          pagerMeta={writable ? "Draft · unsaved changes" : "Read-only"}
        />
      ) : (
        <div className="tpl-editor__skeleton" aria-busy="true">
          Loading draft content…
        </div>
      )}
      {canWrite && (
        <div className="dre__actions">
          <Button variant="primary" onClick={handleSave} disabled={save.isPending || !writable}>
            {save.isPending ? "Saving…" : "Save version"}
          </Button>
          {save.isError && (
            <Alert label="Couldn't save" tone="error">
              {getApiErrorMessage(save.error) ?? save.error.message}
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}

/** Scroll + outline the clause the inline review publishes (reviewFocus) inside
 *  the editor under the returned host ref — when that focus is for `draftId`.
 *  Shared by the Home rail editor and the Draft Workspace's main editor. */
export function useReviewFocusScroll(draftId: string): RefObject<HTMLDivElement> {
  const focus = useSyncExternalStore(
    subscribeReviewFocus,
    getReviewFocus,
    getReviewFocus
  );
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focus === null || focus.draftId !== draftId) return;
    const host = hostRef.current;
    if (host === null) return;
    // The CKEditor editable mounts async; retry briefly until it (and the
    // clause) exist, then scroll.
    let tries = 0;
    let timer = 0;
    let observer: MutationObserver | null = null;
    // [review-scroll 2026-10-15] Bill Jacks: "I'm going to approve the change. And
    // I got to scroll back down to where that was." Apply change saves a new
    // version and the editor reloads it — the editable's blocks are replaced (or
    // the editor remounts behind a loading skeleton): the outline goes and the
    // scroll resets to the top. Reproduced (e2e/draft-review-scroll.spec.ts).
    // While a clause is in focus, watch the stable host: once the outline is
    // gone, put the clause back in view. Typing never fires this — the outline
    // is still there.
    const watch = (): void => {
      if (observer !== null) return;
      observer = new MutationObserver(() => {
        if (host.querySelector(".cs-clause-live") !== null) return;
        window.clearTimeout(timer);
        tries = 0;
        timer = window.setTimeout(attempt, 50);
      });
      observer.observe(host, { childList: true, subtree: true });
    };
    const attempt = (): void => {
      const editable = host.querySelector<HTMLElement>(".ck-editor__editable");
      if (editable !== null && scrollToClause(editable, focus)) {
        watch();
        return;
      }
      if (tries < 10) {
        tries += 1;
        timer = window.setTimeout(attempt, 200);
      }
    };
    attempt();
    return () => {
      window.clearTimeout(timer);
      observer?.disconnect();
    };
  }, [focus, draftId]);
  return hostRef;
}

// [after-review-inline P2b] Find the clause under review in the rendered draft
// and scroll it into view + outline it. The draft has no per-clause anchors, so
// locate by the clause's own text (most specific) with a heading-name fallback.
// Returns false when nothing matched yet (the editor is still mounting).
function scrollToClause(editable: HTMLElement, focus: NonNullable<ReviewFocus>): boolean {
  const norm = (s: string): string =>
    s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  const stripLead = (s: string): string => s.replace(/^[\s\d.]+/, "");
  const isHeading = (el: HTMLElement): boolean => /^h[1-6]$/i.test(el.tagName);

  const name = stripLead(norm(focus.name));
  // The clause's own body text, past any duplicated heading — a distinctive,
  // unique anchor (a body paragraph, unlike a bare clause name, appears once).
  let body = norm(focus.text);
  if (name.length >= 3) {
    const at = body.indexOf(name);
    if (at >= 0 && at < 24) body = body.slice(at + name.length);
  }
  body = stripLead(body).trim();
  const probe = body.slice(0, 45);

  const blocks = Array.from(
    editable.querySelectorAll<HTMLElement>("h1, h2, h3, h4, h5, h6, p, li, blockquote")
  );
  let target: HTMLElement | null = null;
  // 1) The HEADING whose text matches the clause name — the real section anchor.
  //    Restricting to headings is what stops a body paragraph that merely
  //    *mentions* the name (e.g. "…the Fixed Compensation…", which sits before
  //    the actual "5. FIXED COMPENSATION" heading) from being highlighted.
  if (name.length >= 3) {
    target =
      blocks.find(
        (b) => isHeading(b) && stripLead(norm(b.textContent ?? "")).startsWith(name)
      ) ?? null;
  }
  // 2) Else a block containing the clause's distinctive BODY text.
  if (target === null && probe.length >= 15) {
    target = blocks.find((b) => norm(b.textContent ?? "").includes(probe)) ?? null;
  }
  // 3) Last resort: a block whose text STARTS WITH the name (never mid-paragraph).
  if (target === null && name.length >= 3) {
    target =
      blocks.find((b) => stripLead(norm(b.textContent ?? "")).startsWith(name)) ?? null;
  }
  if (target === null) return false;

  // Anchor on the clause HEADING: if we matched a body block, walk back to the
  // nearest preceding heading sibling.
  let section = target;
  if (!isHeading(section)) {
    let prev = section.previousElementSibling;
    for (let k = 0; k < 6 && prev !== null; k += 1) {
      if (prev instanceof HTMLElement && isHeading(prev)) {
        section = prev;
        break;
      }
      prev = prev.previousElementSibling;
    }
  }

  // [P4] Highlight the WHOLE clause — the text that gets replaced — not just the
  // heading: the heading block plus every following sibling up to the next
  // heading (the clause body). If we never found a heading (section is a bare
  // paragraph), just highlight that block.
  editable
    .querySelectorAll(".cs-clause-live")
    .forEach((el) => el.classList.remove("cs-clause-live"));
  const clauseBlocks: HTMLElement[] = [section];
  if (isHeading(section)) {
    let next = section.nextElementSibling;
    while (next !== null && !(next instanceof HTMLElement && isHeading(next))) {
      if (next instanceof HTMLElement) clauseBlocks.push(next);
      next = next.nextElementSibling;
    }
  }
  clauseBlocks.forEach((b) => b.classList.add("cs-clause-live"));

  // Scroll WITHIN the editable (not the page) so the clause sits near the top.
  const er = editable.getBoundingClientRect();
  const tr = section.getBoundingClientRect();
  editable.scrollTop += tr.top - er.top - 64;
  return true;
}
